/**
 * Held ASSET props (q-npc-props-adoption-and-flame): an `NPC_AMBIENT_PROPS` row may put a
 * model of an asset pack straight into an NPC's hand — the hand-sized smith hammer, the
 * torch, the basket, the tankard of `npc-hand-props-v1` — instead of an item's equipment
 * binding. The row declares its grip as a hand-local offset in METRES and radians; this
 * module turns that into the bone-local transform the rig needs and seats the prop.
 *
 * The rig's bones live under a centimetre armature (`RIG_BONE_UNIT_SCALE`), exactly as for
 * a player's weapon (rig-avatar/sockets.ts), so the offset is scaled by it and the prop gets
 * the reciprocal scale to keep its metre size. The bone is the slot's CHR-026 socket
 * (`RIG_SOCKET_BONE`: mainhand → RightHand, offhand → LeftHand), resolved through the
 * loader's name sanitisation (`resolveRigBone`).
 *
 * Node-safe: no `three` runtime import; the bone lookup is injected.
 */

import type { Object3D } from 'three';

import type {
  NpcHeldAssetProp,
  NpcHeldItemProp,
  NpcHeldProp,
  NpcPropGrip,
} from '@great-cloud/shared/client';

import { applySocket, type SocketTransform } from '../equipment/sockets.js';
import {
  RIG_BONE_UNIT_SCALE,
  RIG_SOCKET_BONE,
  resolveRigBone,
  type BoneLookup,
} from '../rig-avatar/sockets.js';

/** True for a row that names an asset-pack model (not an item). */
export function isHeldAssetProp(row: NpcHeldProp): row is NpcHeldAssetProp {
  return 'assetPack' in row;
}

/** True for a row that names an item drawn through its equipment binding. */
export function isHeldItemProp(row: NpcHeldProp): row is NpcHeldItemProp {
  return 'item' in row;
}

/** The bone-local transform of a declared grip (metres/radians → the rig's units). */
export function heldAssetTransform(grip: NpcPropGrip | undefined): SocketTransform {
  const [px, py, pz] = grip?.position ?? [0, 0, 0];
  const [rx, ry, rz] = grip?.rotation ?? [0, 0, 0];
  return {
    position: [px * RIG_BONE_UNIT_SCALE, py * RIG_BONE_UNIT_SCALE, pz * RIG_BONE_UNIT_SCALE],
    rotation: [rx, ry, rz],
    scale: RIG_BONE_UNIT_SCALE,
  };
}

/** The bone a slot's prop parents to, or null when the rig has no such socket. */
export function heldAssetBone(bone: BoneLookup, slot: string): Object3D | null {
  const canonical = RIG_SOCKET_BONE[slot];
  if (canonical === undefined) return null;
  const found = resolveRigBone(bone, canonical);
  return (found as unknown as Object3D | null | undefined) ?? null;
}

/**
 * Seat a loaded prop in the hand: the grip transform, parented to the slot's bone. Returns
 * false (the prop untouched) when the rig has no bone for the slot.
 */
export function seatHeldAsset(prop: Object3D, bone: BoneLookup, row: NpcHeldAssetProp): boolean {
  const parent = heldAssetBone(bone, row.slot);
  if (parent === null || typeof parent.add !== 'function') return false;
  applySocket(prop, heldAssetTransform(row.grip));
  parent.add(prop);
  return true;
}
